import { Divider } from '@material-ui/core';
import { Theme } from '@material-ui/core/styles';
import { createStyles, makeStyles } from '@material-ui/styles';
import clsx from 'clsx';
import React from 'react';
import { FormattedMessage } from 'react-intl';
import { useSelector } from 'react-redux';

import { ReactComponent as FPSIcon } from '@/assets/icons/fps-big.svg';
import { HeaderTitle, Label, MoneyAmountValue, Space } from '@/components';
import { StatusLayout } from '@/layout';
import { makeSelectIsMobileView } from '@/model/global/selectors';
import ResultFields from '@/pages/withdrawal/pages/StatusPage/components/ResultFields';

import { useFormHeight, useOptionsSize } from './hooks';
import { SuccessViewProps } from './types';

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    contentBox: {},
    contentBoxMobile: {
      overflow: 'visible',
    },
    contentForm: {
      minHeight: '130px',
    },
    amount: {
      fontSize: '36px',
      fontStyle: 'normal',
      fontWeight: 400,
      lineHeight: '30px',
      letterSpacing: '-0.23999999463558197px',
    },
    amountPositiveSuccess: {
      color: theme.colors.accent,
    },
    fieldsBox: {
      overflow: 'auto',
      flex: 1,
    },
    fieldsBoxContainer: {
      overflow: 'inherit',
    },
    footer: {
      flexShrink: 1,
      paddingTop: theme.spacing(3),
      backgroundColor: theme.colors.lightBackground,
      overflow: 'inherit',
    },
  }),
);

const selectIsMobile = makeSelectIsMobileView();

const SuccessView: React.FC<SuccessViewProps> = ({ Options, basePath, transaction, action, 'data-test': dataTest }) => {
  const classes = useStyles();
  const formHeight = useFormHeight({
    transaction,
    hasAction: !!action,
    hasOptions: !!Options,
  });
  const { ref, size } = useOptionsSize();
  const isMobile = useSelector(selectIsMobile);
  const amount = transaction.amount || 0;
  const showOptionsDivider = size === 'xl' || size === 'l' || size === 'm';
  const smallPadding = size === 'xs' || size === 's';

  return (
    <StatusLayout
      data-test={dataTest}
      minHeight={400}
      height={formHeight}
      status="success"
      footer={
        Options
          ? {
              className: classes.footer,
              component: (
                <ResultFields
                  data-test={dataTest && `${dataTest}-fields`}
                  data={transaction}
                  isCredit={transaction.isCredit}
                  className={classes.fieldsBox}
                />
              ),
            }
          : undefined
      }
      classnames={{
        contentForm: classes.contentForm,
      }}
      buttons={{
        main: action
          ? {
              submit: false,
              onAct: action.run,
              title: action.title,
            }
          : undefined,
      }}
      header={{
        title: (
          <HeaderTitle
            data-test={dataTest && `${dataTest}-title`}
            title={
              transaction?.isFPS ? (
                <FormattedMessage id="withdrawal.pages.result.title" />
              ) : (
                <FormattedMessage id="demo.pages.transaction.common.title" />
              )
            }
            postfix={isMobile && transaction.isFPS && <FPSIcon />}
          />
        ),
      }}
    >
      <Space
        ref={ref}
        direction="vertical"
        /* eslint-disable-next-line no-nested-ternary */
        size={smallPadding ? 1 : isMobile ? 3 : 4}
        className={isMobile ? classes.contentBoxMobile : classes.contentBox}
      >
        <Space
          direction="vertical"
          size={isMobile ? 1 : 2}
          style={{
            alignItems: 'center',
            flexShrink: 0,
          }}
        >
          <MoneyAmountValue
            data-test={dataTest && `${dataTest}-amount`}
            withSign
            value={amount}
            className={clsx(classes.amount, amount > 0 && classes.amountPositiveSuccess)}
            withCurrency
            fraction={2}
          />
          <Label data-test={dataTest && `${dataTest}-result`}>
            <FormattedMessage id="withdrawal.pages.result.success.title" />
          </Label>
        </Space>
        {Options ? (
          <>
            {showOptionsDivider && <Divider orientation="horizontal" />}
            <Space
              direction="horizontal"
              size={0}
              style={{
                justifyContent: 'center',
                flexShrink: 0,
              }}
            >
              <Options
                data-test={dataTest && `${dataTest}-options`}
                size={size}
                transactionId={transaction.id}
                basePath={basePath}
              />
            </Space>
          </>
        ) : (
          <ResultFields
            data-test={dataTest && `${dataTest}-fields`}
            data={transaction}
            isCredit={transaction.isCredit}
            className={classes.fieldsBox}
          />
        )}
      </Space>
    </StatusLayout>
  );
};

export default SuccessView;
